import { useEffect, useMemo, useState } from 'react';
import type { CwChargeCodeAliasDto } from '@catalyst-psa/shared';

import { Button } from '../../components/ui/Button.js';
import { useCwChargeCodeAliases, useSetCwChargeCodeAliases } from './api.js';

interface AliasRow {
  /** Stable client-side key so rows keep focus while editing. */
  id: string;
  cwChargeCodeId: string;
  cwChargeCodeName: string;
  serviceCategoryId: string;
  billingClassId: string;
  ticketId: string;
}

let aliasSeq = 0;
function newAliasId(): string {
  aliasSeq += 1;
  return `chargecode-alias-${aliasSeq}`;
}

interface ChargeCodeAliasEditorProps {
  /** Whether the parent modal is open (gates the query). */
  isOpen: boolean;
}

/**
 * Maps a ConnectWise charge code onto a fixed trio of PSA targets: a service category, a
 * billing class, and a ticket. On the time-entries sync, an entry whose ConnectWise
 * `chargeToType` is `ChargeCode` is resolved through these rows by its charge code id —
 * the mapped ticket supplies the synced entry's ticket (and its project/phase), and the
 * mapped service category and billing class override the normal resolvers. A charge-code
 * entry without a row here is skipped and logged.
 */
export function ChargeCodeAliasEditor({ isOpen }: ChargeCodeAliasEditorProps): JSX.Element {
  const configQuery = useCwChargeCodeAliases(isOpen);
  const saveAliases = useSetCwChargeCodeAliases();

  const [rows, setRows] = useState<AliasRow[]>([]);
  const config = configQuery.data;

  useEffect(() => {
    if (!config) return;
    setRows(
      config.aliases.map((alias) => ({
        id: newAliasId(),
        cwChargeCodeId: alias.cwChargeCodeId,
        cwChargeCodeName: alias.cwChargeCodeName ?? '',
        serviceCategoryId: alias.serviceCategoryId,
        billingClassId: alias.billingClassId,
        ticketId: alias.ticketId,
      })),
    );
  }, [config]);

  const serviceCategories = config?.serviceCategories ?? [];
  const billingClasses = config?.billingClasses ?? [];
  const tickets = config?.tickets ?? [];
  const discoveredChargeCodes = config?.discoveredChargeCodes ?? [];
  const live = config?.live ?? false;

  // Combined charge-code options: those discovered live plus any saved-alias codes that
  // are not in the live sample, so a saved row's code is always selectable.
  const chargeCodeOptions = useMemo(() => {
    const byId = new Map<string, { id: string; name: string }>();
    for (const code of discoveredChargeCodes) byId.set(code.id, code);
    for (const row of rows) {
      if (row.cwChargeCodeId && !byId.has(row.cwChargeCodeId)) {
        byId.set(row.cwChargeCodeId, {
          id: row.cwChargeCodeId,
          name: row.cwChargeCodeName || row.cwChargeCodeId,
        });
      }
    }
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [discoveredChargeCodes, rows]);

  // CW charge codes already mapped, so we can hint which discovered ones remain.
  const mappedIds = useMemo(
    () => new Set(rows.map((row) => row.cwChargeCodeId).filter(Boolean)),
    [rows],
  );
  const unmappedDiscovered = discoveredChargeCodes.filter((code) => !mappedIds.has(code.id));

  const updateRow = (id: string, patch: Partial<AliasRow>): void => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const setRowChargeCode = (id: string, chargeCodeId: string): void => {
    const match = chargeCodeOptions.find((code) => code.id === chargeCodeId);
    updateRow(id, {
      cwChargeCodeId: chargeCodeId,
      cwChargeCodeName: match?.name ?? '',
    });
  };

  const removeRow = (id: string): void => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const addRow = (code?: { id: string; name: string }): void => {
    setRows((prev) => [
      ...prev,
      {
        id: newAliasId(),
        cwChargeCodeId: code?.id ?? '',
        cwChargeCodeName: code?.name ?? '',
        serviceCategoryId: '',
        billingClassId: '',
        ticketId: '',
      },
    ]);
  };

  const handleSave = (): void => {
    const aliases: CwChargeCodeAliasDto[] = rows
      .filter(
        (row) =>
          row.cwChargeCodeId && row.serviceCategoryId && row.billingClassId && row.ticketId,
      )
      .map((row) => ({
        cwChargeCodeId: row.cwChargeCodeId,
        cwChargeCodeName: row.cwChargeCodeName.trim() || undefined,
        serviceCategoryId: row.serviceCategoryId,
        billingClassId: row.billingClassId,
        ticketId: row.ticketId,
      }));
    saveAliases.mutate({ aliases });
  };

  const inputClass =
    'w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <div>
        <h3 className="text-sm font-semibold text-slate-800">Charge code mapping</h3>
        <p className="mt-1 text-sm text-slate-500">
          ConnectWise time can be logged against a charge code instead of a ticket. PSA time always
          belongs to a ticket, so map each ConnectWise charge code to a PSA service category,
          billing class, and ticket below. On sync, charge-code time uses these instead of the
          normal resolvers — charge codes without a row here are skipped and logged.
        </p>
      </div>

      {configQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading charge codes…</p>
      ) : configQuery.isError ? (
        <p className="text-sm text-rose-600">Failed to load charge codes.</p>
      ) : (
        <>
          {live ? (
            <p className="text-xs text-emerald-600">
              ConnectWise charge codes loaded live from your connection.
            </p>
          ) : (
            <p className="text-xs text-slate-400">
              No ConnectWise charge codes discovered yet. Configure the connection to load them.
            </p>
          )}

          {saveAliases.isError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              Could not save the charge-code mappings. Make sure each row has a charge code, service
              category, billing class, and ticket.
            </div>
          ) : null}

          {rows.length === 0 ? (
            <p className="text-sm text-slate-400">No charge-code mappings yet. Add one below.</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="py-2 pr-3 font-medium">Charge code</th>
                  <th className="py-2 pr-3 font-medium">Service category</th>
                  <th className="py-2 pr-3 font-medium">Billing class</th>
                  <th className="py-2 pr-3 font-medium">Ticket</th>
                  <th className="py-2 font-medium sr-only">Remove</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="py-2 pr-3 align-top">
                      <select
                        value={row.cwChargeCodeId}
                        onChange={(e) => setRowChargeCode(row.id, e.target.value)}
                        className={inputClass}
                      >
                        <option value="">— Select charge code —</option>
                        {chargeCodeOptions.map((code) => (
                          <option key={code.id} value={code.id}>
                            {code.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 pr-3 align-top">
                      <select
                        value={row.serviceCategoryId}
                        onChange={(e) => updateRow(row.id, { serviceCategoryId: e.target.value })}
                        className={inputClass}
                      >
                        <option value="">— Select category —</option>
                        {serviceCategories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 pr-3 align-top">
                      <select
                        value={row.billingClassId}
                        onChange={(e) => updateRow(row.id, { billingClassId: e.target.value })}
                        className={inputClass}
                      >
                        <option value="">— Select class —</option>
                        {billingClasses.map((billingClass) => (
                          <option key={billingClass.id} value={billingClass.id}>
                            {billingClass.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 pr-3 align-top">
                      <select
                        value={row.ticketId}
                        onChange={(e) => updateRow(row.id, { ticketId: e.target.value })}
                        className={inputClass}
                      >
                        <option value="">— Select ticket —</option>
                        {tickets.map((ticket) => (
                          <option key={ticket.id} value={ticket.id}>
                            #{ticket.number} — {ticket.title}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 align-top">
                      <button
                        type="button"
                        onClick={() => removeRow(row.id)}
                        className="mt-1.5 text-xs font-medium text-slate-400 hover:text-rose-600"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => addRow()}>
              Add mapping
            </Button>
            {unmappedDiscovered.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
                <span>Unmapped:</span>
                {unmappedDiscovered.map((code) => (
                  <button
                    key={code.id}
                    type="button"
                    onClick={() => addRow(code)}
                    className="rounded-full border border-slate-300 bg-white px-2 py-0.5 hover:border-brand-400 hover:text-brand-700"
                  >
                    {code.name}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="ml-auto">
              <Button onClick={handleSave} isLoading={saveAliases.isPending} disabled={!config}>
                Save charge codes
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
